import { useEffect, useMemo, useState } from "react";
import { organizerGetImportableConfigApi, organizerListImportableLocalesApi } from "../../api/api";
import { MAIN_API } from "../../api/apilist";
import "../primerUso/primerUso.css";

const errorText = (e) => e?.response?.data?.message || e?.message || "No pudimos completar la operación. Probá de nuevo.";

const resolveUrl = (u) => {
  if (!u) return "";
  if (/^(https?:|blob:|data:)/i.test(u)) return u;
  return u.startsWith("/") ? `${MAIN_API}${u}` : `${MAIN_API}/${u}`;
};

/** Locales grouped by negocio, in the order the back sent them (already sorted by negocio then name). */
export const agruparPorNegocio = (locales) => {
  const grupos = [];
  for (const l of Array.isArray(locales) ? locales : []) {
    const clave = l.organizer?.id || l.organizer?.name || "";
    let g = grupos.find((x) => x.clave === clave);
    if (!g) {
      g = { clave, negocio: l.organizer?.name || "Otro negocio", locales: [] };
      grupos.push(g);
    }
    g.locales.push(l);
  }
  return grupos;
};

/**
 * Picker of the local whose configuration is copied into a new one. Lists the locales of the same
 * `tipo` ("club" | "bar" | "beauty") from related negocios (same titular or casa central), grouped by
 * negocio, and on choosing one loads its sanitized config and hands it to `onImport(config)`.
 */
const ImportarConfigLocalModal = ({ tipo, onImport, onClose }) => {
  const [estado, setEstado] = useState({ cargando: true, error: "", locales: [] });
  const [intento, setIntento] = useState(0);
  const [eligiendo, setEligiendo] = useState("");
  const [errorElegir, setErrorElegir] = useState("");

  useEffect(() => {
    let activo = true;
    setEstado({ cargando: true, error: "", locales: [] });
    (async () => {
      try {
        const res = await organizerListImportableLocalesApi(tipo);
        const locales = Array.isArray(res?.data?.info) ? res.data.info : [];
        if (activo) setEstado({ cargando: false, error: "", locales });
      } catch (e) {
        if (activo) setEstado({ cargando: false, error: errorText(e), locales: [] });
      }
    })();
    return () => {
      activo = false;
    };
  }, [tipo, intento]);

  const grupos = useMemo(() => agruparPorNegocio(estado.locales), [estado.locales]);

  const elegir = async (local) => {
    setEligiendo(local.id);
    setErrorElegir("");
    try {
      const res = await organizerGetImportableConfigApi(local.id);
      const config = res?.data?.info;
      if (!config?.local) throw new Error("No pudimos leer la configuración de ese local.");
      onImport({ ...config, tipo: config.tipo || tipo, ajustes: config.ajustes || [] });
    } catch (e) {
      setErrorElegir(errorText(e));
      setEligiendo("");
    }
  };

  return (
    <div className="pu-modal-overlay" onMouseDown={eligiendo ? undefined : onClose}>
      <div
        className="pu-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pu-import-titulo"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 id="pu-import-titulo" className="pu-modal__title">Copiar configuración de otro local</h3>
        <p className="pu-modal__lead">
          Elegí un local de tu mismo titular o casa central. Vas a poder revisar todo antes de guardar.
        </p>

        {estado.cargando && <p className="pu-empty" role="status">Cargando locales…</p>}

        {!estado.cargando && estado.error && (
          <div className="ef-alert ef-alert--danger" role="alert">
            {estado.error}{" "}
            <button type="button" className="pu-link" onClick={() => setIntento((n) => n + 1)}>Reintentar</button>
          </div>
        )}

        {!estado.cargando && !estado.error && grupos.length === 0 && (
          <p className="pu-empty">No hay locales de tu mismo titular o casa central para copiar.</p>
        )}

        {errorElegir && <div className="ef-alert ef-alert--danger" role="alert">{errorElegir}</div>}

        <div className="pu-modal__list">
          {grupos.map((g) => (
            <section key={g.clave} aria-label={g.negocio}>
              <h4 className="pu-modal__group">{g.negocio}</h4>
              <ul className="pu-import-list">
                {g.locales.map((l) => (
                  <li key={l.id}>
                    <button
                      type="button"
                      className="pu-import-item"
                      disabled={Boolean(eligiendo)}
                      onClick={() => elegir(l)}
                    >
                      {l.image ? (
                        <img src={resolveUrl(l.image)} alt="" className="pu-import-item__img" />
                      ) : (
                        <span className="pu-import-item__img" />
                      )}
                      <span className="pu-check-txt">
                        <b>{l.name}</b>
                        <small>{[l.address, l.city].filter(Boolean).join(" · ")}</small>
                      </span>
                      {eligiendo === l.id && <small>Copiando…</small>}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="pu-actions">
          <span />
          <button type="button" className="ef-btn ef-btn--ghost" onClick={onClose} disabled={Boolean(eligiendo)}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportarConfigLocalModal;
